// ============================================================================
// CW-Q Phase 6 — rate basis, rendered explicitly (2026-08-14).
//
// `v_facility_margins.rate_basis` (CW-K / TCSD-0033) already answers the
// question "why does this building have no monthly rate":
//
//   'monthly_flat' → facilities.monthly_rate > 0. 193 buildings.
//   'hourly'       → billing_type in ('Hourly','Hourly_BuddyPunch'), or the
//                    building has client_billing_lines with hours > 0. The
//                    month's revenue is actuals, so monthly_rate is NULL by
//                    design. 14 buildings, every one of them a hospital.
//   'unrated'      → neither. Nobody has recorded a rate. 3 buildings.
//
// Nothing in the app read that column. The facility card did
// `facility.monthly_rate ?? null` and handed it to InlineEditField, which
// renders its `(not set)` placeholder for NULL — so Lee's Summit, Menorah,
// Research Medical Center and eleven more said the same thing as a building
// with a genuine data gap. In the list view it was worse: fmtCurrency(null)
// returns '' and the rate simply vanished.
//
// "(not set)" on an hourly hospital is a false statement. The rate IS set. It
// is set in hours. This module is the single place that distinction lives, so
// no component re-derives it and no component has to know the enum values.
//
// Deliberately NOT in scope (CW-K prohibition, restated in the CW-Q brief):
// the `clusters` CTE cost-group fix, and the `is_billing_primary = false →
// NULL` margin attribution. Nothing here changes a margin number.
// ============================================================================

export type RateBasis = 'monthly_flat' | 'hourly' | 'unrated';

/** Every basis the view can emit. Iteration order is display order. */
export const RATE_BASES: readonly RateBasis[] = ['monthly_flat', 'hourly', 'unrated'] as const;

/**
 * Where an hourly building's revenue actually comes from. One string, one
 * place — the card, the list and any future surface must say the same thing.
 */
export const HOURLY_ACTUALS_LABEL = 'Billed on actual hours';

/**
 * Coerce whatever the view/PostgREST handed us into a known basis. An
 * unrecognised or missing value degrades to 'unrated' — the honest answer —
 * and never to 'monthly_flat', which would invite a $0.
 */
export function normalizeRateBasis(v: string | null | undefined): RateBasis {
  return v === 'monthly_flat' || v === 'hourly' || v === 'unrated' ? v : 'unrated';
}

/**
 * Fallback for when the v_facility_margins read failed (the card is defensive
 * about that — see use-facility-info-data.ts) and all we have is the raw
 * facilities.monthly_rate.
 *
 * This can only ever return 'monthly_flat' or 'unrated'. 'hourly' is a claim
 * about HOW a client is billed and we will not invent it from a NULL; a NULL
 * rate with no view row is a building we cannot characterise, which is exactly
 * what 'unrated' says.
 */
export function deriveRateBasisFallback(monthlyRate: number | null | undefined): RateBasis {
  return monthlyRate != null && monthlyRate > 0 ? 'monthly_flat' : 'unrated';
}

/** Short label, always non-empty, safe to render as a standalone chip. */
export function rateBasisLabel(basis: RateBasis): string {
  switch (basis) {
    case 'monthly_flat':
      return 'Monthly flat rate';
    case 'hourly':
      return 'Hourly';
    case 'unrated':
      return 'No rate recorded';
  }
}

/**
 * One-word chip for beside a field label, where `rateBasisLabel` would repeat
 * the value verbatim. Observed 2026-08-14 on FAC-0328: chip "No rate recorded"
 * directly above value "No rate recorded" read as a stutter, not as emphasis.
 */
export function rateBasisChip(basis: RateBasis): string {
  switch (basis) {
    case 'monthly_flat':
      return 'Monthly';
    case 'hourly':
      return 'Hourly';
    case 'unrated':
      return 'Unrated';
  }
}

/**
 * Longer sentence for a hint/caption. Says what to do about it, because a
 * label alone made an AM think the number was missing rather than absent by
 * design.
 */
export function rateBasisHint(basis: RateBasis): string {
  switch (basis) {
    case 'monthly_flat':
      // Deliberately not "changes need approval" — the Financial summary card
      // already says that in its footer, and a caption that repeats the
      // paragraph below it teaches the reader to skip captions.
      return 'Flat monthly rate agreed with the client — the same figure every month.';
    case 'hourly':
      return `${HOURLY_ACTUALS_LABEL} — this building has no monthly rate by design. The month's revenue comes from its client billing lines (hospital AR logbook), not from this field.`;
    case 'unrated':
      return 'Nobody has recorded a client rate for this building. This is a data gap, not an hourly arrangement — ask the account manager.';
  }
}

/** Formats the way the Financial summary card always has: no forced cents. */
function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export interface ClientRateDisplay {
  /** The basis actually used after the guards below. */
  basis: RateBasis;
  /** What to render in the value slot. Never '' and never '$0'. */
  text: string;
  /** Caption under the value. */
  hint: string;
  /** True only when `text` is a dollar figure the reader may do maths on. */
  isAmount: boolean;
}

/**
 * The client-rate cell, decided in one place.
 *
 * Two guards matter more than the happy path:
 *   1. basis 'monthly_flat' with a NULL/0/negative rate is a contradiction —
 *      the view derives 'monthly_flat' FROM monthly_rate > 0, so if we ever
 *      see it we are looking at stale or partial data. It degrades to
 *      'unrated' rather than printing `$0`.
 *   2. basis 'hourly' NEVER prints a figure from this field, even if some
 *      future write leaves a stray monthly_rate on an hourly building. The
 *      revenue is elsewhere; a number here would be double-counted by eye.
 */
export function clientRateDisplay(input: {
  basis: RateBasis;
  monthlyRate: number | null | undefined;
  /**
   * Items 831/758: the hourly rate of the building's running line billed by
   * the hour, when it has exactly one such rate. Read from the LINE, never from
   * the building's billing type. Omitted or null: just "Billed on actual hours".
   */
  hourlyRate?: number | null;
}): ClientRateDisplay {
  const { monthlyRate } = input;

  if (input.basis === 'hourly') {
    const rate = hasCents(input.hourlyRate) ? (input.hourlyRate as number) : null;
    return {
      basis: 'hourly',
      text: hourlyBilledText(rate),
      hint: rate != null ? HOURLY_LINE_HINT : rateBasisHint('hourly'),
      isAmount: false,
    };
  }

  // Round to cents BEFORE deciding whether a rate exists. `money()` renders at
  // most 2 decimal places, so a rate of 0.004 is > 0 but formats as "$0" — the
  // exact render this phase exists to abolish, arrived at from a technically
  // non-zero number. A rate that cannot be displayed as anything but $0 is not
  // a rate. Formatting from the rounded value keeps the text and the test of
  // the text talking about the same number.
  const cents = monthlyRate == null ? null : Math.round(monthlyRate * 100);
  const hasRate = cents != null && cents > 0;
  if (input.basis === 'monthly_flat' && hasRate) {
    return {
      basis: 'monthly_flat',
      text: `${money((cents as number) / 100)}/mo`,
      hint: rateBasisHint('monthly_flat'),
      isAmount: true,
    };
  }

  return {
    basis: 'unrated',
    text: rateBasisLabel('unrated'),
    hint: rateBasisHint('unrated'),
    isAmount: false,
  };
}

// ============================================================================
// Item 904 (TCSD-0077: a paused building is left out of the totals but kept
// on the screen, marked). A paused line's money is $0 while paused — that
// part is the existing active-only arithmetic, untouched — and the figure it
// keeps for resume is quoted beside it, so the screen never again reads
// "$3,150 coming in, $0 going out" with no explanation.
// ============================================================================

/** "$3,150.00" — always two decimals, for a figure quoted against a $0. */
export function moneyWithCents(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * What a paused figure reads once service resumes — "$3,150.00 when service
 * resumes", with a percent alongside for the margin. Null when nothing is
 * recorded to resume to (a paused line with no rates, e.g. Lake Lucien).
 */
export function resumesAtHint(amount: number | null | undefined, pct?: number | null): string | null {
  if (amount == null) return null;
  const base = pct != null ? `${moneyWithCents(amount)} · ${(pct * 100).toFixed(1)}%` : moneyWithCents(amount);
  return `${base} when service resumes`;
}

export interface MarginAvailability {
  /** True when a margin figure may be shown at all. */
  available: boolean;
  /** Why not, when `available` is false. Always a full sentence. */
  reason: string | null;
}

/**
 * Whether a per-building margin means anything yet, and if not, why.
 *
 * The card used to render '—' for every one of these cases, which put an
 * hourly hospital carrying $3,150/mo of sub cost next to a blank margin and
 * left the reader to guess whether that was a bug. It also matches
 * v_facility_margins' own gate: no margin without BOTH sides.
 */
export function marginAvailability(input: {
  basis: RateBasis;
  clientRate: number | null | undefined;
  subTotal: number | null | undefined;
}): MarginAvailability {
  const { basis, clientRate, subTotal } = input;

  if (basis === 'hourly') {
    return {
      available: false,
      reason:
        'Margin is not computed here for an hourly building — the revenue side is actual hours, so it lives with the billing lines, not on this card.',
    };
  }
  if (clientRate == null || clientRate <= 0) {
    return {
      available: false,
      reason: 'No client rate recorded, so there is no revenue side to measure margin against.',
    };
  }
  if (subTotal == null || subTotal <= 0) {
    return {
      available: false,
      reason:
        'No sub cost recorded on the active assignments at this building, so a margin here would read as 100% and be wrong.',
    };
  }
  return { available: true, reason: null };
}

// ============================================================================
// Items 831 and 758 (Ben 2026-10-12 and 2026-10-13, TCSD-0492): a building
// billed by the hour says its money PER HOUR, keeps no made-up monthly price or
// monthly cleaner cost, and shows its schedule estimate worked out each time and
// never saved. Every screen reads these words from here: the onboarding card,
// the board, the list, the summary email, the account card's service line and
// the building's money section.
// ============================================================================

/** True when the figure exists once rounded to cents (a $0.004 rate is no rate). */
function hasCents(n: number | null | undefined): boolean {
  return n != null && Number.isFinite(n) && Math.round(n * 100) > 0;
}

/** A line is billed by the hour when ITS billing method says so — never the building's billing type. */
export function isHourlyBilledLine(line: { billing_method?: string | null } | null | undefined): boolean {
  return line?.billing_method === 'Hourly';
}

/** A line pays its sub by the hour when its sub rate type says so. */
export function paysSubByTheHour(line: { sub_rate_type?: string | null } | null | undefined): boolean {
  return line?.sub_rate_type === 'hourly';
}

/** "$26.00 an hour" with cents (the card's line figures), "$26 an hour" without (the building row). */
export function perHour(n: number, opts: { cents?: boolean } = { cents: true }): string {
  const s = opts.cents === false
    ? n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `$${s} an hour`;
}

/** "$26.00/hr" — the short form for a board card or a list cell. */
export function perHourShort(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr`;
}

/**
 * "Billed on actual hours — $26 an hour", or just "Billed on actual hours" when
 * there is no single hourly rate to name (Menorah bills by the hospital's own
 * departments). The building row drops cents, as it always has; the onboarding
 * card keeps them.
 */
export function hourlyBilledText(rate: number | null | undefined, opts: { cents?: boolean } = { cents: false }): string {
  return hasCents(rate) ? `${HOURLY_ACTUALS_LABEL} — ${perHour(rate as number, opts)}` : HOURLY_ACTUALS_LABEL;
}

/** Caption under an hourly building's client rate when its line names the rate. */
export const HOURLY_LINE_HINT = 'The client pays for the hours worked each month. There is no monthly rate.';

/** Caption on the onboarding card's price box for an hourly card. */
export const HOURLY_CARD_PRICE_HINT =
  'This building has no monthly price by design: the client pays for the hours worked. Change the hourly rate on the Contractor tab.';

/** Caption under the onboarding card's Economics box for an hourly card. */
export const HOURLY_ECONOMICS_CAPTION =
  'Billed on actual hours, so there is no monthly figure. The margin is the same whatever the hours.';

/** Under "Subcontractor contract rate" on an hourly line with no set hours. */
export const NO_SET_HOURS_SENTENCE = 'No monthly figure: this line has no set hours.';

/** The trigger's refusal, word for word, so a screen can recognise it (TCS_HOURLY_SUB_NO_MONTHLY_FIGURE). */
export const HOURLY_SUB_REFUSAL =
  'This line pays the sub by the hour. Change the hourly rate; a monthly figure is worked out only when the hours are set.';

/**
 * What a schedule comes to in a month: rate × hours a day × days a week × 52 ÷ 12,
 * to the cent. NULL when any input is missing — unknown hours are not zero
 * hours. Worked out on the page each time and NEVER saved: it is an estimate,
 * not a rate (758 option A).
 */
export function scheduleMonthlyEstimate(
  rate: number | null | undefined,
  hoursPerDay: number | null | undefined,
  daysPerWeek: number | null | undefined,
): number | null {
  if (!hasCents(rate) || hoursPerDay == null || daysPerWeek == null) return null;
  if (!(hoursPerDay > 0) || !(daysPerWeek > 0)) return null;
  return Math.round(((rate as number) * hoursPerDay * daysPerWeek * 52 * 100) / 12) / 100;
}

/** "About $4,506.67 a month" */
export function aboutAMonth(n: number): string {
  return `About $${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} a month`;
}

/** "8 hours a day × 5 days a week × $26" — the estimate's working, named. */
export function scheduleWorking(rate: number, hoursPerDay: number, daysPerWeek: number): string {
  const r = rate.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  const h = hoursPerDay.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return `${h} hour${hoursPerDay === 1 ? '' : 's'} a day × ${daysPerWeek} day${daysPerWeek === 1 ? '' : 's'} a week × $${r}`;
}

/** The caption under "What the schedule comes to". */
export function scheduleEstimateCaption(rate: number, hoursPerDay: number, daysPerWeek: number): string {
  return `${scheduleWorking(rate, hoursPerDay, daysPerWeek)}, averaged over the year. An estimate for planning — not a rate, and not what the client is charged.`;
}

/**
 * The single hourly rate a building's running lines billed by the hour carry,
 * or null when it has none or they disagree (then the row just says "Billed on
 * actual hours" rather than picking one).
 */
export function runningHourlyRate(
  lines: ReadonlyArray<{ billing_method?: string | null; billing_rate?: number | null; assignment_status?: string | null }>,
): number | null {
  const rates = new Set<number>();
  for (const l of lines) {
    if (l.assignment_status === 'Active' && isHourlyBilledLine(l) && hasCents(l.billing_rate)) {
      rates.add(Math.round((l.billing_rate as number) * 100));
    }
  }
  return rates.size === 1 ? [...rates][0] / 100 : null;
}

/** Per-hour economics for an onboarding card: client, sub, profit an hour and the margin. */
export interface HourlyEconomics {
  clientPerHour: number | null;
  subPerHour: number | null;
  profitPerHour: number | null;
  /** Percent, one decimal. Null without both sides. */
  marginPct: number | null;
}

export function hourlyEconomics(clientPerHour: number | null | undefined, subPerHour: number | null | undefined): HourlyEconomics {
  const c = hasCents(clientPerHour) ? (clientPerHour as number) : null;
  const s = hasCents(subPerHour) ? (subPerHour as number) : null;
  const profit = c != null && s != null ? Math.round((c - s) * 100) / 100 : null;
  const pct = c != null && profit != null ? Math.round((profit / c) * 1000) / 10 : null;
  return { clientPerHour: c, subPerHour: s, profitPerHour: profit, marginPct: pct };
}

const NOT_RUNNING = new Set(['Ended', 'Inactive', 'Terminated']);

function numOrNull(v: unknown): number | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function single(values: Array<number | null>): number | null {
  const set = new Set(values.filter((v): v is number => v != null && hasCents(v)).map((v) => Math.round(v * 100)));
  return set.size === 1 ? [...set][0] / 100 : null;
}

/**
 * An onboarding card is HOURLY when every one of its lines still running or
 * booked is billed by the hour (read from the card's own `service_lines`, the
 * line's billing method — never the building's billing type). Returns the one
 * client hourly rate and the one sub hourly rate those lines carry (null when
 * none or they disagree), or null for a card that is not hourly.
 *
 * ⛔ A card that mixes hourly and monthly lines is NOT hourly: no combined
 * figure is invented for it (none exists today, measured 2026-10-14).
 */
export function hourlyCardRates(
  serviceLines: ReadonlyArray<Record<string, unknown>> | null | undefined,
): { client: number | null; sub: number | null } | null {
  const running = (serviceLines ?? []).filter((l) => !NOT_RUNNING.has(String(l.assignment_status ?? '')));
  if (running.length === 0) return null;
  if (!running.every((l) => l.billing_method === 'Hourly')) return null;
  return {
    client: single(running.map((l) => numOrNull(l.client_rate))),
    sub: single(running.filter((l) => l.sub_rate_type === 'hourly').map((l) => numOrNull(l.sub_hourly_rate))),
  };
}
